/**
 * dataStore.ts
 * Module-level persistent stores for Activities, News, Votes, Files.
 * State survives component unmount/remount (navigation), enabling cross-module sync.
 */

import type { AssocFile, Vote } from "../../../api/types";
import type { ActivityRegistrationOptionsConfig } from "../../../utils/activityRegistrationOptions";
import type { ActivityTargetAudience } from "../../../utils/activityTargetAudience";

/* ─── Activity ─────────────────────────────────── */
export type ActivityStatus = "ongoing" | "full" | "draft" | "ended" | "pending";

export interface ActivitySummary {
  rating: number;
  summary: string;
  highlights: string;
  improvements: string;
  nextSuggestion: string;
  submittedAt: string;
}

export interface Activity {
  id: string;
  title: string;
  activityType: string;
  date: string;
  /** 活动结束时间（ISO 8601，与后端 eventEndDate 一致） */
  eventEndDate?: string;
  location: string;
  checkInFenceLat?: number;
  checkInFenceLng?: number;
  checkInFenceRadiusMeters?: number;
  checkInEarlyMinutes?: number;
  checkInLateMinutes?: number | null;
  requireCheckOut?: boolean;
  checkOutAllowEarly?: boolean;
  checkOutEarlyMinutes?: number;
  capacity: number;
  enrolled: number;
  memberEnrolled?: number;
  guestEnrolled?: number;
  status: ActivityStatus;
  regStartDate?: string;
  regEndDate?: string;
  expenseType?: string;
  onlinePayAmountFen?: number;
  onlinePayDepositAmountFen?: number;
  onlinePayAmountsLocked?: boolean;
  allowCancelWithRefund?: boolean;
  cancelDeadlineMode?: string;
  cancelDeadlineAt?: string;
  cancelDeadlineResolvedAt?: string;
  registrationOptions?: ActivityRegistrationOptionsConfig;
  allowMemberFriendGuestRegistration?: boolean;
  memberFriendGuestRegistrationQuota?: number;
  showEnrollmentCountToMembers?: boolean;
  showShareOnRegistrationPage?: boolean;
  targetAudience?: ActivityTargetAudience;
  /** 关联协会参与对象（发布方配置） */
  linkedAssociations?: Array<{
    tenantId: string;
    tenantName?: string;
    shortName?: string;
    quota: number;
    enrolled?: number;
  }>;
  /** 他协会授权本协会报名的活动（勿混入本协会活动管理列表） */
  isLinkedActivity?: boolean;
  description?: string;
  tags?: string;
  requireDocSign?: boolean;
  requiredDocIds?: string[];
  activitySummary?: ActivitySummary;
  activitySummaryJson?: string | null;
  lotteryEnabled?: boolean;
  rejectionNote?: string;
  submittedAt?: string;
  approvedAt?: string;
  participationPoints?: number;
  enableParticipationPointDeduction?: boolean;
  notRegisteredDeductionPoints?: number;
  notCheckedInDeductionPoints?: number;
  coverUrl?: string;
  ageMin?: number;
  ageMax?: number;
}

const _activities: Activity[] = [];

const _activityListeners = new Set<() => void>();

export const activityStore = {
  getAll: (): Activity[] => [..._activities],
  add: (item: Activity) => {
    _activities.unshift(item);
    _activityListeners.forEach((fn) => fn());
  },
  update: (id: string, changes: Partial<Activity>) => {
    const idx = _activities.findIndex((a) => a.id === id);
    if (idx !== -1) {
      Object.assign(_activities[idx], changes);
      _activityListeners.forEach((fn) => fn());
    }
  },
  remove: (id: string) => {
    const idx = _activities.findIndex((a) => a.id === id);
    if (idx !== -1) {
      _activities.splice(idx, 1);
      _activityListeners.forEach((fn) => fn());
    }
  },
  subscribe: (fn: () => void): (() => void) => {
    _activityListeners.add(fn);
    return () => _activityListeners.delete(fn);
  },
};

/* ─── News ─────────────────────────────────────── */
export interface NewsItem {
  id: string;
  title: string;
  category: string;
  author: string;
  status: string;
  publishDate: string;
  readCount: number;
  summary?: string;
  content?: string;
  coverUrl?: string;
  scheduledAt?: string; // ISO datetime string for scheduled publish
}

const _news: NewsItem[] = [];

export const newsStore = {
  getAll: (): NewsItem[] => [..._news],
  add: (item: NewsItem) => { _news.unshift(item); },
  update: (id: string, changes: Partial<NewsItem>) => {
    const idx = _news.findIndex((n) => n.id === id);
    if (idx !== -1) Object.assign(_news[idx], changes);
  },
  remove: (id: string) => {
    const idx = _news.findIndex((n) => n.id === id);
    if (idx !== -1) _news.splice(idx, 1);
  },
};

/* ─── Votes ─────────────────────────────────────── */
const _votes: Vote[] = [];

export const voteStore = {
  getAll: (): Vote[] => [..._votes],
  add: (item: Vote) => { _votes.unshift(item); },
  update: (id: string, changes: Partial<Vote>) => {
    const idx = _votes.findIndex((v) => v.id === id);
    if (idx !== -1) Object.assign(_votes[idx], changes);
  },
  remove: (id: string) => {
    const idx = _votes.findIndex((v) => v.id === id);
    if (idx !== -1) _votes.splice(idx, 1);
  },
};

/* ─── Files ─────────────────────────────────────── */
const _files: AssocFile[] = [];
const _fileListeners = new Set<() => void>();

export const fileStore = {
  getAll: (): AssocFile[] => [..._files],
  add: (item: AssocFile) => {
    _files.unshift(item);
    _fileListeners.forEach((fn) => fn());
  },
  update: (id: string, changes: Partial<AssocFile>) => {
    const idx = _files.findIndex((f) => f.id === id);
    if (idx !== -1) {
      Object.assign(_files[idx], changes);
      _fileListeners.forEach((fn) => fn());
    }
  },
  /** 新建或替换一条文件（与 POST/PUT /files 响应一致），便于保存后立即更新列表而不依赖整页 reload */
  upsert: (item: AssocFile) => {
    const idx = _files.findIndex((f) => f.id === item.id);
    if (idx >= 0) _files[idx] = item;
    else _files.unshift(item);
    _fileListeners.forEach((fn) => fn());
  },
  subscribe: (fn: () => void): (() => void) => {
    _fileListeners.add(fn);
    return () => _fileListeners.delete(fn);
  },
};

/** 用后端数据替换内存中的活动 / 资讯 / 投票 / 文件 */
export function replaceStoresFromApi(payload: {
  activities: Activity[];
  news: NewsItem[];
  votes: Vote[];
  files: AssocFile[];
}) {
  _activities.length = 0;
  _activities.push(...payload.activities);
  _news.length = 0;
  _news.push(...payload.news);
  _votes.length = 0;
  _votes.push(...payload.votes);
  _files.length = 0;
  _files.push(...payload.files);
  _fileListeners.forEach((fn) => fn());
  _activityListeners.forEach((fn) => fn());
}

/** 仅同步投票列表（会员端投票页在未打开管理后台时也可拉取） */
export function replaceVotesFromApi(votes: Vote[]) {
  _votes.length = 0;
  _votes.push(...votes);
}
